import React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { DataGrid, type DataGridColumn } from '@heroui-pro/react';
import type { LocaleCode } from '@/api/models';
import {
    buildDataGridSkeletonColumns,
    buildDataGridSkeletonRows,
} from '@/components';
import { cn } from '@/utils/cnUtils';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import {
    ACTIVITY_STATUS_CHIP,
    type ActivityRowModel,
} from '../../activityMeta';

type ActivityTableProps = {
    locale: LocaleCode;
    rows: ActivityRowModel[];
    onSelect: (recordId: string) => void;
    /**
     * The register is still in flight. The grid keeps its real column headers
     * and skeletons only the rows — an empty list while loading means "not known
     * yet", never "nothing was recorded".
     */
    isLoading: boolean;
};

const SKELETON_ROW_COUNT = 5;

/**
 * The register of what has been recorded.
 *
 * A HeroUI Pro `DataGrid`, like every other list screen in this folder: it
 * renders a real `<table>` with genuine `columnheader`s and a `rowheader` on the
 * operation, so the register still reads as a ledger listing. Activating a row
 * opens the detail drawer (`onRowAction`), which is the journal table's idiom.
 *
 * **The operation is named in both locales, stacked** — the selected UI
 * language as the row title with the other language under it, exactly as the
 * Templates tab names the same operations. A register whose rows named an
 * operation differently from the catalog that configured it would make the two
 * look like different things.
 *
 * **A reversed row is not de-emphasised.** The employee register mutes a
 * terminated row because the employment is history; a reversed operation is not
 * history, it is a fact with a correction attached, and both halves are things a
 * reader may be looking for. The chip carries the difference.
 */
export const ActivityTable: React.FC<ActivityTableProps> = ({
    locale,
    rows,
    onSelect,
    isLoading,
}) => {
    const { t } = useTranslation('pages');

    const columns = React.useMemo<DataGridColumn<ActivityRowModel>[]>(
        () => [
            {
                id: 'operation',
                header: t('operations.activity.columns.operation'),
                isRowHeader: true,
                minWidth: 220,
                cell: (row) => (
                    <span className="flex flex-col">
                        <span className="font-medium text-foreground">
                            {locale === 'ka' ? row.nameKa : row.nameEn}
                        </span>
                        <span className="text-xs text-muted">
                            {locale === 'ka' ? row.nameEn : row.nameKa} ·{' '}
                            {row.versionLabel}
                        </span>
                    </span>
                ),
            },
            {
                id: 'amount',
                header: t('operations.activity.columns.amount'),
                align: 'end',
                minWidth: 130,
                cell: (row) => (
                    <span className="whitespace-nowrap font-medium tabular-nums text-foreground">
                        {row.amountText}
                    </span>
                ),
            },
            {
                id: 'actor',
                header: t('operations.activity.columns.actor'),
                minWidth: 150,
                cell: (row) => (
                    <span className="text-foreground">{row.actorText}</span>
                ),
            },
            {
                id: 'date',
                header: t('operations.activity.columns.date'),
                minWidth: 150,
                cell: (row) => (
                    <span className="flex flex-col">
                        <span className="whitespace-nowrap tabular-nums text-foreground">
                            {row.dateText}
                        </span>
                        <span className="whitespace-nowrap text-xs tabular-nums text-muted">
                            {row.entryNumberText}
                        </span>
                    </span>
                ),
            },
            {
                id: 'status',
                header: t('operations.activity.columns.status'),
                minWidth: 120,
                cell: (row) => {
                    const chip = ACTIVITY_STATUS_CHIP[row.status];
                    return (
                        <span
                            className={cn(
                                'inline-flex w-fit items-center rounded-lg px-2.5 py-1 text-xs font-medium',
                                chip.className,
                            )}
                            {...getNodeTestIdProp(
                                `operations-activity-status-${row.id}`,
                            )}
                        >
                            {t(chip.labelKey)}
                        </span>
                    );
                },
            },
            {
                id: 'reference',
                header: t('operations.activity.columns.reference'),
                minWidth: 130,
                cell: (row) => (
                    <span className="whitespace-nowrap text-muted">
                        {row.referenceText}
                    </span>
                ),
            },
        ],
        [locale, t],
    );

    // `contents` wrapper: `DataGrid` forwards no unknown props, so a spread
    // testid never reaches the DOM. `display: contents` adds no box, so the
    // grid's layout and scoped CSS are untouched.
    return (
        <div
            className="contents"
            {...getNodeTestIdProp('operations-activity-table')}
        >
            {isLoading ? (
                <DataGrid<{ id: string }>
                    aria-label={t('operations.activity.tableAria')}
                    className="ledger-data-grid"
                    columns={buildDataGridSkeletonColumns(columns)}
                    contentClassName="min-w-[900px]"
                    data={buildDataGridSkeletonRows(
                        SKELETON_ROW_COUNT,
                        'operations-activity',
                    )}
                    getRowId={(row) => row.id}
                />
            ) : (
                <DataGrid<ActivityRowModel>
                    aria-label={t('operations.activity.tableAria')}
                    className="ledger-data-grid"
                    columns={columns}
                    contentClassName="min-w-[900px]"
                    data={rows}
                    getRowId={(row) => row.id}
                    onRowAction={(key) => onSelect(String(key))}
                    renderEmptyState={() => (
                        <div
                            className="flex flex-col items-center gap-3 py-12 text-muted"
                            {...getNodeTestIdProp(
                                'operations-activity-table-empty',
                            )}
                        >
                            <Icon
                                aria-hidden
                                className="size-8 text-default-500"
                                icon="gravity-ui:folder-open"
                            />
                            <span className="max-w-sm text-center text-sm">
                                {t('operations.activity.empty')}
                            </span>
                        </div>
                    )}
                />
            )}
        </div>
    );
};
